import React, { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import Lottie from 'lottie-react';

import { useLaneChooserTheme } from '@/playlist/games/laneChooser/theme';

import CorrectArrow from '@/playlist/games/laneChooser/assets/images/correct-arrow.svg?react';

export type AnswerBoxProps = {
	label?: string;
	isActive: boolean;
	isCorrect: boolean;
	position?: 'left' | 'right' | 'middle';
	showCorrect?: boolean;
	paused?: boolean;
	deltaSeconds?: number;
};

const AnswerBox = ({
	label = '',
	isActive,
	isCorrect,
	position = 'middle',
	showCorrect = false,
	paused = false,
	deltaSeconds,
}: AnswerBoxProps) => {
	const { animations } = useLaneChooserTheme();
	const { answerBoxes } = animations;

	const showDelta = isActive;
	const hasPositiveReward = (deltaSeconds ?? 0) > 0;
	const iconName = isCorrect ? 'icon-correct-green' : 'icon-incorrect-red';
	const deltaText = isCorrect ? (hasPositiveReward ? `+${deltaSeconds}` : null) : '-10';

	// Select the correct animation source based on position
	const boxSource = useMemo(() => {
		if (position === 'left') return answerBoxes.left;
		if (position === 'right') return answerBoxes.right;
		return answerBoxes.middle;
	}, [answerBoxes.left, answerBoxes.middle, answerBoxes.right, position]);

	// Check if source is SVG (string) or Lottie (object)
	const isSvg = typeof boxSource === 'string';
	const animationData = !isSvg ? (boxSource as unknown as object) : null;

	const lottieRef = useRef<any>(null);

	useEffect(() => {
		const api = lottieRef.current as any;
		if (!api) return;
		if (paused) {
			api.stop();
		} else {
			api.play();
		}
	}, [paused]);

	const trimmedLabel = label.trim();
	const isShort = trimmedLabel.length > 0 && trimmedLabel.length <= 5;
	const labelClassName = classNames('lane-chooser-answer__label', {
		'lane-chooser-answer__label--short': isShort,
		'lane-chooser-answer__label--long': !isShort,
	});

	return (
		<div
			className={classNames('lane-chooser-answer__box', position, {
				'lane-chooser-answer__box--success': isActive && isCorrect,
				'lane-chooser-answer__box--failed': isActive && !isCorrect,
				'lane-chooser-answer__box--correct': showCorrect,
				'lane-chooser-answer__box--svg': isSvg,
			})}
		>
			{isSvg ? (
				<img
					src={boxSource as string}
					alt=""
					className={isActive ? 'lane-chooser-answer__box-active' : undefined}
				/>
			) : (
				<Lottie
					animationData={animationData}
					autoplay
					loop
					className={isActive ? 'lane-chooser-answer__box-active' : undefined}
					lottieRef={lottieRef}
				/>
			)}
			{label && <div className={labelClassName}>{label}</div>}
			{showDelta && (
				<div className="lane-chooser-answer__delta">
					<span className={`icon ${iconName}`} />
					<span className="lane-chooser__answer-delta-text">{deltaText}</span>
				</div>
			)}
			{showCorrect && !isActive && (
				<div className="lane-chooser-answer__correct">
					<CorrectArrow />
				</div>
			)}
		</div>
	);
};

export default AnswerBox;
